import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Switch,
  Button,
  Divider,
} from "@mui/material";
import {
  ImageOutlined,
  AnalyticsOutlined,
  EventOutlined,
  PersonAddOutlined,
  LockOutlined,
  MenuBookOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import MyQuill from "../quill/MyQuill";
import LanguageIcon from "@mui/icons-material/Language";
import { ADMIN, API_ENDPOINT } from "../utils/constants";
import { getHeader, toastError, validateEmail } from "../utils/util";
import axios from "axios";
import { toast } from "react-toastify";
import { LoadingButton } from "@mui/lab";
import useGetEventSettings from "../hooks/useGetEventSettings";
import useCatalogueFormStatus from "../hooks/useCatalogueFormStatus";
const SettingsPage = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useGetEventSettings();
  const {
    data: cFormData,
    isLoading: isFormDataLoading,
    isError: isCformError,
  } = useCatalogueFormStatus();
  const [senderEmail, setSenderEmail] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);

  const [savingSettings, setSavingSettings] = useState(false);
  const [settings, setSettings] = useState({
    eventStartDate: null,
    eventEndDate: null,
    visitorRegStartDate: null,
    visitorRegEndDate: null,
    disableVisitorReg: false,
    disableExhibitionCatalogue: false,
    visitorRegMessage: "",
    exhibitionCatalogueMessage: "",
  });

  useEffect(() => {
    if (data && data.emailTemplate) {
      // Assuming the data has a senderEmail property
      setSenderEmail(data.emailTemplate.senderEmail);
    }
  }, [data]);

  useEffect(() => {
    if (cFormData) {
      setSettings({
        ...settings,
        disableVisitorReg: cFormData.disableVisitorReg,
        disableExhibitionCatalogue: cFormData.disableExhibitionCatalogue,
        visitorRegMessage: cFormData.visitorRegMessage,
        exhibitionCatalogueMessage: cFormData.exhibitionCatalogueMessage,
      });
    }
  }, [cFormData]);

  const handleDateChange = (field) => (date) => {
    setSettings((prev) => ({ ...prev, [field]: date }));
  };

  const handleSwitchChange = (field) => (event) => {
    setSettings((prev) => ({
      ...prev,
      [field]: event.target.checked,
    }));
  };

  const handleMessageChange = (field) => (newValue) => {
    setSettings((prev) => ({
      ...prev,
      [field]: newValue,
    }));
  };

  const validateSettings = () => {
    if (settings.disableVisitorReg && !settings.visitorRegMessage.trim()) {
      alert("Please provide a message for disabled visitor registration");
      return false;
    }
    if (
      settings.disableExhibitionCatalogue &&
      !settings.exhibitionCatalogueMessage.trim()
    ) {
      alert("Please provide a message for disabled exhibition catalogue");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateSettings()) return;
    try {
      setSavingSettings(true);
      const { data } = await axios.put(
        `${API_ENDPOINT}admin/save-settings`,
        settings,
        getHeader(ADMIN)
      );
      toast.success(data.message);
    } catch (error) {
      toastError(error);
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Settings Page
        </Typography>

        <Grid container spacing={3}>
          {/* Banner Icon Settings */}
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ImageOutlined />}
              onClick={() => navigate("/home/banner-logo-settings")}
              sx={{ justifyContent: "flex-start", py: 1.5 }}
            >
              Banner Icon Settings
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<LanguageIcon />}
              onClick={() => navigate("/home/meta-data-editor")}
              sx={{ justifyContent: "flex-start", py: 1.5 }}
            >
              App Site Title description
            </Button>
          </Grid>

          {/* Analytics Tag Settings */}
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AnalyticsOutlined />}
              onClick={() => navigate("/home/update-gtag")}
              sx={{ justifyContent: "flex-start", py: 1.5 }}
            >
              Analytics Tag Settings
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Event Dates */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              <EventOutlined sx={{ verticalAlign: "middle", mr: 1 }} />
              Event Dates
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <DatePicker
                  label="Start Date"
                  value={settings.eventStartDate}
                  onChange={handleDateChange("eventStartDate")}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
                <DatePicker
                  label="End Date"
                  value={settings.eventEndDate}
                  onChange={handleDateChange("eventEndDate")}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Box>
            </LocalizationProvider>
          </Grid>

          {/* Visitor Registration Dates */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              <PersonAddOutlined sx={{ verticalAlign: "middle", mr: 1 }} />
              Visitor Registration Dates
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <DatePicker
                  label="Start Date"
                  value={settings.visitorRegStartDate}
                  onChange={handleDateChange("visitorRegStartDate")}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
                <DatePicker
                  label="End Date"
                  value={settings.visitorRegEndDate}
                  onChange={handleDateChange("visitorRegEndDate")}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Box>
            </LocalizationProvider>
          </Grid>

          {/* Disable Visitor Registration */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <LockOutlined sx={{ mr: 1 }} />
              <Typography variant="body1" sx={{ flexGrow: 1 }}>
                Disable Visitor Registration Form
              </Typography>
              <Switch
                checked={settings.disableVisitorReg}
                onChange={handleSwitchChange("disableVisitorReg")}
              />
            </Box>
            {settings.disableVisitorReg && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Message to display when registration is disabled:
                </Typography>
                <MyQuill
                  value={settings.visitorRegMessage}
                  onChange={handleMessageChange("visitorRegMessage")}
                />
              </Box>
            )}
          </Grid>

          {/* Disable Exhibition Catalogue */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <MenuBookOutlined sx={{ mr: 1 }} />
              <Typography variant="body1" sx={{ flexGrow: 1 }}>
                Disable Exhibition Catalogue
              </Typography>
              <Switch
                checked={settings.disableExhibitionCatalogue}
                onChange={handleSwitchChange("disableExhibitionCatalogue")}
              />
            </Box>
            {settings.disableExhibitionCatalogue && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Message to display when catalogue is disabled:
                </Typography>
                <MyQuill
                  value={settings.exhibitionCatalogueMessage}
                  onChange={handleMessageChange("exhibitionCatalogueMessage")}
                />
              </Box>
            )}
          </Grid>

          {/* Save Button */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                sx={{ mt: 2 }}
              >
                Save Settings
              </Button>
            </Box>
          </Grid>
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6">Update Sender Email</Typography>
        <TextField
          sx={{ mb: 2, mt: 1 }}
          fullWidth
          value={senderEmail}
          error={senderEmail && !validateEmail(senderEmail)}
          helperText={
            senderEmail && !validateEmail(senderEmail) && "Invalid email!"
          }
          onChange={(event) => setSenderEmail(event.target.value)}
          variant="outlined"
          size="small"
          label="Sender Email"
        />
        <LoadingButton
          loading={savingEmail}
          onClick={async () => {
            try {
              setSavingEmail(true);
              const { data } = await axios.post(
                `${API_ENDPOINT}admin/update-sender-email`,
                { email: senderEmail },
                getHeader(ADMIN)
              );
              refetch();
              toast.success(data.message);
            } catch (error) {
              toastError(error);
            } finally {
              setSavingEmail(false);
            }
          }}
          variant="contained"
          size="small"
        >
          Save
        </LoadingButton>
      </Paper>
    </Container>
  );
};

export default SettingsPage;
